import { useMemo } from "react";
import { Activity, TrendingUp, AlertTriangle, CheckCircle2 } from "lucide-react";
import { predictSlump } from "@/lib/ai/slumpPredictor";

/**
 * AI Slump Prediction card.
 * Feed it either a live batch (via the `batch` prop) or a static example
 * for a demo screen. When `batch` + `mix` are provided we derive the inputs
 * from the batch's captured actuals + mix design targets. Anything missing
 * falls back to a sensible mix-book default so the card always renders.
 */
export default function SlumpCard({ batch, mix, sampleOverrides }) {
  const inputs = useMemo(() => buildInputs(batch, mix, sampleOverrides), [batch, mix, sampleOverrides]);
  const result = useMemo(() => predictSlump(inputs), [inputs]);

  const delta = result.predictedSlump - inputs.targetSlump;
  const status =
    Math.abs(delta) <= 10 ? "ok" :
    delta > 10 ? "over" : "under";

  const chip = {
    ok:    { icon: CheckCircle2,   text: "IN SPEC",     colour: "text-emerald-400 border-emerald-500/60 bg-emerald-500/10" },
    under: { icon: TrendingUp,     text: "UNDER TARGET", colour: "text-amber-400 border-amber-500/60 bg-amber-500/10" },
    over:  { icon: AlertTriangle,  text: "OVER TARGET",  colour: "text-red-400 border-red-500/60 bg-red-500/10" },
  }[status];
  const ChipIcon = chip.icon;

  return (
    <div className="border border-industrial bg-surface p-4" data-testid="slump-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary-orange" />
          <span className="mono text-[10px] uppercase tracking-[0.28em] text-primary-orange font-black">AI Slump Prediction</span>
        </div>
        <span className={`mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border inline-flex items-center gap-1 ${chip.colour}`} data-testid="slump-status">
          <ChipIcon className="w-2.5 h-2.5" /> {chip.text}
        </span>
      </div>

      <div className="grid grid-cols-[1fr_auto] items-end gap-4">
        <div>
          <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Predicted slump</div>
          <div className="mono text-5xl font-black text-primary-orange leading-none tabular-nums" data-testid="slump-value">
            {result.predictedSlump}
            <span className="text-xl text-muted-industrial ml-1">mm</span>
          </div>
          <div className="mono text-[10px] text-muted-industrial mt-1">
            Target · <span className="text-ink font-black">{inputs.targetSlump} mm</span> · Δ {delta >= 0 ? "+" : ""}{delta} mm
          </div>
        </div>
        <div className="text-right">
          <div className="mono text-[9px] uppercase tracking-widest text-muted-industrial">Confidence</div>
          <div className="mono text-2xl font-black text-ink leading-none tabular-nums">{result.confidence}%</div>
        </div>
      </div>

      <div className={`mt-3 border-l-2 pl-2 mono text-[11px] leading-snug ${
        status === "ok" ? "border-emerald-400 text-emerald-400" :
        status === "over" ? "border-red-400 text-red-400" :
        "border-amber-400 text-amber-400"
      }`} data-testid="slump-recommendation">
        {result.recommendation}
      </div>

      {/* Live inputs — small, so batchers can trust what the model is seeing. */}
      <details className="mt-3 mono text-[10px] text-muted-industrial">
        <summary className="cursor-pointer hover:text-primary-orange uppercase tracking-widest">Model inputs</summary>
        <div className="grid grid-cols-3 gap-x-3 gap-y-1 mt-2">
          <Kv label="Water"        v={`${inputs.water} L`} />
          <Kv label="Cement"       v={`${inputs.cement} kg`} />
          <Kv label="Plasticiser"  v={`${inputs.plasticiser} L`} />
          <Kv label="Sand H₂O"     v={`${inputs.sandMoisture}%`} />
          <Kv label="Stone H₂O"    v={`${inputs.stoneMoisture}%`} />
          <Kv label="Air"          v={`${inputs.airTemp}°C`} />
          <Kv label="Concrete"     v={`${inputs.concreteTemp}°C`} />
          <Kv label="Mix time"     v={`${inputs.mixTime}s`} />
          <Kv label="Target"       v={`${inputs.targetSlump} mm`} />
        </div>
      </details>
    </div>
  );
}

function Kv({ label, v }) {
  return (
    <div>
      <div className="text-muted-industrial uppercase tracking-widest text-[8px]">{label}</div>
      <div className="text-ink font-black tabular-nums">{v}</div>
    </div>
  );
}

/**
 * Derive the model inputs from a live batch + mix design. Falls back to a
 * standard mix-book default for anything that isn't captured yet so the card
 * never renders NaN.
 */
function buildInputs(batch, mix, overrides = {}) {
  const q = Number(batch?.quantity_m3 || 1) || 1;
  const capturedKg = (key) => Number(batch?.actual_weights?.[key] || 0);
  const targetKg   = (key) => Number(batch?.target_weights?.[key] || 0);
  const pick       = (k, fb) => (capturedKg(k) || targetKg(k) || fb);

  // Convert per-batch totals into per-m³ figures the coefficients were tuned for
  const waterKg    = pick("water", 180 * q) / q;
  const cementKg   = pick("cement", 320 * q) / q;
  // Plasticiser is stored in ml; convert to L
  const plasticiserMl = capturedKg("wrda_pn40") || capturedKg("plasticiser") || targetKg("wrda_pn40") || (0.5 * q * 1000);
  const plasticiserL  = plasticiserMl / 1000 / q;

  return {
    targetSlump:    Number(mix?.slump_mm ?? overrides.targetSlump ?? 100),
    water:          Math.round(waterKg),
    cement:         Math.round(cementKg),
    sandMoisture:   Number(overrides.sandMoisture ?? 4.5),
    stoneMoisture:  Number(overrides.stoneMoisture ?? 1.2),
    plasticiser:    Number(plasticiserL.toFixed(2)),
    airTemp:        Number(overrides.airTemp ?? 22),
    concreteTemp:   Number(overrides.concreteTemp ?? 25),
    mixTime:        Number(overrides.mixTime ?? 65),
  };
}
